import { ECardinality, ESdChangeReason, ESymbolCategory, IAction, IActionParameter } from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import ActionParameterTitle from "./action-parameter-title";
import { SymbolPicker } from "./symbol-picker";

export interface IPushToCacheSymbolsGroupProps {
  action: IAction;
  parameters: IActionParameter[];
  onSaveAction: (action: IAction, changeReason?: ESdChangeReason) => void;
}

export interface IPushToCacheSymbolsGroupState {
  cacheKeyInput?: string;
}

class PushToCacheSymbolsGroup extends PureSdComponent<
  IPushToCacheSymbolsGroupProps & IWithSDProps & IWithContextProp,
  IPushToCacheSymbolsGroupState
> {
  constructor(props: IPushToCacheSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      cacheKeyInput: this.getCacheKeyValue() ?? ""
    };
  }

  componentDidUpdate(prevProps: IPushToCacheSymbolsGroupProps) {
    const prevKey = prevProps.parameters.find((p) => p.name === "key")?.symbol?.fixedValue;
    const currKey = this.getCacheKeyValue();
    if (prevKey !== currKey && currKey !== this.state.cacheKeyInput) {
      this.setState({ cacheKeyInput: currKey ?? "" });
    }
  }

  toKeyFormat = (value: string) => {
    let result = value.replace(/\s+/g, "_");
    result = _.deburr(result);
    result = result.replace(/[^A-Za-z0-9_]/g, "");
    return result.toUpperCase();
  };

  handleCacheKeyInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (value === "") {
      this.handleCacheKeyClear();
      return;
    }
    value = this.toKeyFormat(value);
    this.setState({ cacheKeyInput: value });
  };

  handleCacheKeyBlur = () => {
    const value = this.toKeyFormat(this.state.cacheKeyInput ?? "");
    const params = _.cloneDeep(this.props.parameters);
    const keyParam = params.find((p) => p.name === "key");
    if (!keyParam) return;
    if (!keyParam.symbol) keyParam.symbol = KMainFunctions.getDefaultSymbol(keyParam.Typename);
    keyParam.symbol.fixedValue = value;
    keyParam.symbol.category = ESymbolCategory.fixed;
    const action: IAction = _.cloneDeep(this.props.action);
    action.parameters = params;
    this.props.onSaveAction(action, ESdChangeReason.ActionParameterFixedValueChanged);
    this.setState({ cacheKeyInput: value });
  };

  handleCacheKeyClear = () => {
    this.setState({ cacheKeyInput: "" });
    const params = _.cloneDeep(this.props.parameters);
    const keyParam = params.find((p) => p.name === "key");
    if (!keyParam) return;
    if (!keyParam.symbol) keyParam.symbol = KMainFunctions.getDefaultSymbol(keyParam.Typename);
    keyParam.symbol.fixedValue = undefined;
    keyParam.symbol.category = ESymbolCategory.fixed;
    const action: IAction = _.cloneDeep(this.props.action);
    action.parameters = params;
    this.props.onSaveAction(action, ESdChangeReason.ActionParameterFixedValueChanged);
  };

  getCacheKeyValue = () => {
    const keyParam = this.props.parameters.find((p) => p.name === "key");
    return keyParam?.symbol?.fixedValue;
  };

  handleCacheItemChange = (symbol: any) => {
    const params = _.cloneDeep(this.props.parameters);
    const cacheItemParam = params.find((p) => p.name === "bagItem");
    if (!cacheItemParam) return;
    cacheItemParam.symbol = symbol;
    const action: IAction = _.cloneDeep(this.props.action);
    action.parameters = params;
    this.props.onSaveAction(action, ESdChangeReason.ActionParameterFixedValueChanged);
  };

  getCacheItemSymbol = () => {
    const cacheItemParam = this.props.parameters.find((p) => p.name === "bagItem");
    return cacheItemParam?.symbol;
  };

  renderKey() {
    return (
      <div>
        <div className="k_Regular-G1-Left kuika_rightPanelDIName" style={{ marginBottom: "6px", marginTop: "8px" }}>
          <ActionParameterTitle parameter={{ name: "Cache Key", Typename: "String", cardinality: ECardinality.One }} />
        </div>
        <KuikaInput
          allowClear
          size="sm"
          style={{ width: "100%" }}
          value={this.state.cacheKeyInput}
          onChange={this.handleCacheKeyInput}
          onBlur={this.handleCacheKeyBlur}
          placeholder="Enter Cache Key"
        />
      </div>
    );
  }

  renderCacheItem() {
    const cacheItemParam: IActionParameter | undefined = this.props.parameters.find(
      (p: IActionParameter): boolean => p.name === "bagItem"
    );

    return (
      <div style={{ marginTop: 12 }}>
        <div className="k_Regular-G1-Left kuika_rightPanelDIName" style={{ marginBottom: "6px" }}>
          Cache Item (Any - Any)
        </div>
        <SymbolPicker
          action={this.props.action}
          symbol={this.getCacheItemSymbol()}
          onSelectedSymbol={this.handleCacheItemChange}
          valueType="Any"
          targetCardinality={ECardinality.Any}
          bindingParameter={cacheItemParam}
          bindingLabel="Cache Item"
        />
      </div>
    );
  }

  render() {
    return (
      <div className="kuika_property-grid-column">
        {this.renderKey()}
        {this.renderCacheItem()}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.ActionParameterFixedValueChanged
    );
  };
}

const pushToCacheSymbolsGroup = withContext<IPushToCacheSymbolsGroupProps>(
  withSD<IPushToCacheSymbolsGroupProps>(PushToCacheSymbolsGroup)
);
export { pushToCacheSymbolsGroup as PushToCacheSymbolsGroup };
